<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: '' },
  // { id, name, subtitle, icon, colors } - the node id is only ever the value, never shown.
  targets: {
    type: Array,
    required: true,
    validator: value => value.every(target => 'id' in target),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const search = ref('');

const visibleTargets = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return props.targets;
  return props.targets.filter(target =>
    `${target.name} ${target.subtitle}`.toLowerCase().includes(query)
  );
});

// Clicking the row that is already the destination clears it, so there is no separate reset
// control competing for space.
const select = id =>
  emit('update:modelValue', id === props.modelValue ? '' : id);
</script>

<template>
  <!-- Laid out in place rather than as a dropdown: this lives inside a native <dialog> opened
       with showModal(), which sits in the browser's top layer - no teleported overlay can render
       above it, and one kept inside the dialog gets clipped by its own scroll container. -->
  <div class="flex flex-col gap-2 min-w-0">
    <div class="relative flex-shrink-0">
      <span
        class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
      />
      <input
        v-model="search"
        type="search"
        :placeholder="t('FLOWS.EDITOR.FIELDS.JUMP_TARGET_SEARCH')"
        class="reset-base w-full py-2 !pl-9 !pr-3 text-sm rounded-lg outline outline-1 outline-n-weak focus:outline-n-brand bg-n-alpha-black2 text-n-slate-12"
      />
    </div>

    <div
      class="flex flex-col gap-0.5 max-h-72 overflow-y-auto rounded-lg border border-n-weak p-1"
    >
      <button
        v-for="target in visibleTargets"
        :key="target.id"
        type="button"
        class="flex items-center gap-3 w-full text-left px-2 py-2 rounded-md border-l-2"
        :class="
          target.id === modelValue
            ? 'border-n-blue-9 bg-n-blue-9/10'
            : 'border-transparent hover:border-n-blue-9 hover:bg-n-slate-3'
        "
        @click="select(target.id)"
      >
        <span
          class="flex-shrink-0 size-8 rounded-lg flex items-center justify-center"
          :class="target.colors.bg"
        >
          <span :class="[target.icon, target.colors.text]" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-medium text-n-slate-12 line-clamp-2">
            {{ target.name }}
          </span>
          <span class="block text-xs text-n-slate-10 truncate">
            {{ target.subtitle }}
          </span>
        </span>
        <span
          v-if="target.id === modelValue"
          class="i-lucide-check flex-shrink-0 text-n-blue-11"
        />
      </button>

      <p
        v-if="!visibleTargets.length"
        class="px-2 py-4 text-sm text-center text-n-slate-10"
      >
        {{ t('FLOWS.EDITOR.PALETTE_NO_RESULTS') }}
      </p>
    </div>
  </div>
</template>
